import { formatCents, formatDecimal, parseMoneyToCents, tt, type CourierQuote } from '@agos/shared'

/**
 * A phone order's delivery fee where external couriers deliver - `FRONTEND.md` §9.786, on BACKEND.md §10A.438 B,
 * owner O139.
 *
 * The owner: the delivery fee **is** the courier's reward (answer 1), and a phone order's typed fee is the reward
 * (the default announced and accepted). So the till prefills the fee from the server's quote for the address, and
 * the person on the phone may change it - what they send is what the courier is offered (`OrderCreationService`
 * records it as `typed`). The zones are not consulted in this mode, on either side.
 */

/**
 * The fee to charge, in cents: what was typed when something readable was, else the quote's, else null - which the
 * till says rather than sending a delivery for nought.
 */
export function tillCourierFeeCents(typed: string, quote: CourierQuote | null): number | null {
  const trimmed = typed.trim().replace(',', '.')
  if (trimmed !== '' && /^\d+(\.\d{1,2})?$/.test(trimmed)) return parseMoneyToCents(trimmed)
  return quote?.feeCents ?? null
}

/** The sentence under the fee box, for each state the quote can be in. */
export function tillCourierNote(
  quote: CourierQuote | null,
  state: { hasPoint: boolean; failed: boolean },
): string {
  if (!state.hasPoint) return tt('Zadajte adresu - odmena šoféra, a teda doprava, sa vypočíta zo vzdialenosti.')
  if (state.failed) return tt('Odmenu šoféra sa nepodarilo vypočítať - zadajte dopravu ručne.')
  if (quote === null || quote.feeCents === null) return tt('Počítam vzdialenosť…')
  const distance = quote.km === null ? '' : `${formatDecimal(quote.km, 1)} km → `
  if (quote.withinRange === false && quote.maxKm !== null) {
    return `${distance}${formatCents(quote.feeCents)}. ${tt('Ďalej, ako jazdia externí šoféri')} (${tt('najviac')} ${formatDecimal(quote.maxKm, 1)} km) - ${tt('objednávku si nemusia vziať.')}`
  }
  return `${tt('Doprava je odmena externého šoféra')}: ${distance}${formatCents(quote.feeCents)}. ${tt('Môžete ju zmeniť.')}`
}
